/** @jsxImportSource ./simboluri-jsx */
// BARELE LONGITUDINALE PE LUNGIMEA AXULUI — ce bară e unde și unde se înnădește.
//
// Diagrama de acoperire de deasupra spune CÂT capabil e la fiecare stație;
// asta spune DIN CE. Sunt aceleași abscise și aceeași scară (funcția `px` vine
// din planșa de deasupra), ca ochiul să poată coborî de pe un vârf de moment
// direct pe bara care îl duce.
//
// O FIBRĂ = O LINIE. Armătura de sus e o linie, cea de jos e o linie — nu un
// rând per traseu. Desenul în trepte (fiecare bară pe rândul ei) arăta bine
// lungimile, dar minte ochiul: barele continue ale deschiderilor vecine nu
// stau la înălțimi diferite, sunt aceeași fibră. Aici linia e una singură, iar
// ce se schimbă pe ea se SCRIE: la fiecare abscisă unde pachetul se schimbă
// (intră o suplimentară, se termină una) stă un reper cu barele care lucrează
// DE ACOLO ÎNAINTE. Zonele de suprapunere se marchează peste linie.
//
// Nu inventează nimic: traseele, bucățile și înnădirile sunt exact ce a croit
// Pasul 13 (`croire_armare`), în abscisa axului.

import { CroireAx, TraseuBara } from "./tipuri";
import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import { COTA_MARTOR, mFmt } from "./cote";

const BARA = "#2b5f8a";          // albastrul barelor, ca în secțiuni
const INNADIRE = "#b8860b";      // suprapunerea
const INNADIRE_FORTATA = "#b5323d";

/** Fibra pe care stă traseul, din rolul scris de Pasul 13. */
function fibra(rol: string): "sus" | "jos" {
  return /sus/i.test(rol) ? "sus" : "jos";
}

/** O bucată de fibră pe care lucrează ACELAȘI pachet de bare. */
type Tronson = {
  s1: number;
  s2: number;
  eticheta: string;
  trasee: TraseuBara[];
};

type Fibra = {
  cheie: "sus" | "jos";
  titlu: Cheie;
  trasee: TraseuBara[];
  tronsoane: Tronson[];
};

/** Pachetul care lucrează pe un tronson: barele, adunate pe diametre.
 *  „4Ø16 + 2Ø20" — în ordinea în care le scrie Pasul 13, adică întâi carcasa
 *  continuă și după ea suplimentarele: așa se și citește secțiunea, bara de
 *  bază plus ce s-a mai pus acolo. */
function pachet(trasee: TraseuBara[]): string {
  const peDiametru = new Map<number, number>();
  for (const t of trasee) {
    const phi = Math.round(t.phi_mm);
    peDiametru.set(phi, (peDiametru.get(phi) || 0) + (Number(t.n) || 0));
  }
  return [...peDiametru.entries()]
    .map(([phi, n]) => `${n}Ø${phi}`)
    .join(" + ");
}

/** Cele două fibre, fiecare tăiată în tronsoanele pe care pachetul rămâne
 *  același. Capetele traseelor sunt singurele abscise la care se poate schimba
 *  ceva, deci ele dau tăieturile; tronsoanele vecine cu același pachet se
 *  lipesc la loc, ca linia să nu fie ruptă degeaba. */
export function fibre(croire: CroireAx): Fibra[] {
  const out: Fibra[] = [];
  for (const f of ["sus", "jos"] as const) {
    const ale_ei = (croire.trasee || []).filter((t) => fibra(t.rol) === f);
    if (!ale_ei.length) continue;

    const taieturi = [...new Set(ale_ei.flatMap((t) => [t.s1_m, t.s2_m]))]
      .sort((a, b) => a - b);
    const tronsoane: Tronson[] = [];
    for (let i = 0; i < taieturi.length - 1; i++) {
      const s1 = taieturi[i];
      const s2 = taieturi[i + 1];
      const mij = (s1 + s2) / 2;
      const active = ale_ei.filter((t) => t.s1_m <= mij && mij < t.s2_m);
      if (!active.length) continue;      // gol de fibră: linia chiar se rupe
      const eticheta = pachet(active);
      const ultim = tronsoane[tronsoane.length - 1];
      if (ultim && ultim.eticheta === eticheta && Math.abs(ultim.s2 - s1) < 1e-6) {
        ultim.s2 = s2;
        continue;
      }
      tronsoane.push({ s1, s2, eticheta, trasee: active });
    }
    out.push({
      cheie: f,
      titlu: f === "sus" ? "struct.bare.fibra_sus" : "struct.bare.fibra_jos",
      trasee: ale_ei,
      tronsoane,
    });
  }
  return out;
}

/** Tronsoanele lipite cap la cap: firul se desenează pe bucăți CONTINUE, ca
 *  rotunjirea capetelor să cadă doar unde armătura chiar se termină. */
function fireContinue(tronsoane: Tronson[]): { s1: number; s2: number }[] {
  const out: { s1: number; s2: number }[] = [];
  for (const tr of tronsoane) {
    const ultim = out[out.length - 1];
    if (ultim && Math.abs(ultim.s2 - tr.s1) < 1e-6) ultim.s2 = tr.s2;
    else out.push({ s1: tr.s1, s2: tr.s2 });
  }
  return out;
}

const H_TITLU = 16;         // banda titlului fibrei
const H_ETICHETE = 26;      // cele două rânduri de repere, deasupra liniei
const H_INNADIRI = 14;      // banda de sub linie, pentru marcajul suprapunerii
const GOL_FIBRA = 10;       // aer între fibre
const GROS = 6;             // grosimea liniei desenate

/** Lățimea aproximativă a unui reper, în px (mono de 9,5). */
function latime(text: string): number {
  return text.length * 5.7 + 6;
}

export default function BareLongitudinale({
  croire,
  px,
  W,
  Ltot,
  evidentiaza,
}: {
  croire: CroireAx;
  /** abscisa axului [m] → x-ul planșei; vine din diagrama de deasupra, ca
   *  desenele să se citească unul sub altul */
  px: (s: number) => number;
  W: number;
  Ltot: number;
  /** deschiderea din pagină: se hașurează pe toată înălțimea */
  evidentiaza?: { s1: number; s2: number } | null;
}) {
  const tt = useT();
  const grupe = fibre(croire);
  if (!grupe.length) return null;

  // înălțimea totală + ordonata liniei fiecărei fibre
  let y = 6;
  const asezate = grupe.map((f) => {
    const yTitlu = y;
    const yBara = yTitlu + H_TITLU + H_ETICHETE;
    y = yBara + H_INNADIRI + GOL_FIBRA;
    return { f, yTitlu, yBara };
  });
  const H = y + 16;

  const noduri = croire.noduri_m || [];

  return (
    <svg viewBox={`0 0 ${W} ${H}`}
         style={{ width: "100%", minWidth: 640, background: "#fff",
                  border: "1px solid var(--line)", borderTop: "none",
                  borderRadius: "0 0 6px 6px", display: "block" }}>
      {/* deschiderea din pagină, pe toată înălțimea — aceeași convenție ca sus */}
      {evidentiaza && (
        <rect x={Math.min(px(evidentiaza.s1), px(evidentiaza.s2))} y={0}
              width={Math.abs(px(evidentiaza.s2) - px(evidentiaza.s1))}
              height={H} fill="#f6f2e8" />
      )}
      {/* reazemele: aceleași abscise ca în planșa de deasupra */}
      {noduri.map((s, i) => (
        <line key={`n${i}`} x1={px(s)} y1={0} x2={px(s)} y2={H - 14}
              stroke={COTA_MARTOR} strokeWidth={0.9} strokeDasharray="4 4" />
      ))}

      {asezate.map(({ f, yTitlu, yBara }) => {
        // reperele: unul la începutul fiecărui tronson. Când două cad prea
        // aproape, al doilea urcă pe rândul de deasupra — textul rămâne
        // lizibil fără să se mute de pe abscisa lui
        const capat = [-1e9, -1e9];      // cât s-a scris pe fiecare rând
        const repere = f.tronsoane.map((tr) => {
          const w = latime(tr.eticheta);
          const x = Math.max(2, Math.min(px(tr.s1) + 3, W - w - 2));
          // rândul de jos (lipit de linie) e cel preferat; când e ocupat,
          // reperul urcă pe cel de deasupra
          const rand = x >= capat[1] + 4 ? 1 : 0;
          capat[rand] = x + w;
          return { tr, x, y: rand === 1 ? yBara - 9 : yBara - 20 };
        });
        return (
          <g key={f.cheie}>
            <text x={4} y={yTitlu + 10} fontSize={10} fontWeight={700}
                  fill="var(--mut)" fontFamily="var(--mono)">
              {tt(f.titlu)}
            </text>

            {/* LINIA: un singur fir pe toată fibra. Capetele rotunjite stau
                doar acolo unde armătura chiar se termină, nu la fiecare
                schimbare de pachet — altfel linia ar părea ruptă în bucăți */}
            {fireContinue(f.tronsoane).map((run, i) => (
              <rect key={`f${i}`} x={px(run.s1)} y={yBara - GROS / 2}
                    width={Math.max(px(run.s2) - px(run.s1), 1)}
                    height={GROS} rx={GROS / 2} fill={BARA} opacity={0.9} />
            ))}
            {/* despărțitorul dintre două pachete + suprafața de hover a
                fiecărui tronson (detaliul barelor lui) */}
            {f.tronsoane.map((tr, i) => (
              <g key={`t${i}`}>
                {i > 0 && Math.abs(f.tronsoane[i - 1].s2 - tr.s1) < 1e-6 && (
                  <line x1={px(tr.s1)} y1={yBara - GROS / 2} x2={px(tr.s1)}
                        y2={yBara + GROS / 2} stroke="#fff" strokeWidth={1.2}
                        opacity={0.85} />
                )}
                <rect x={px(tr.s1)} y={yBara - GROS / 2 - 2}
                      width={Math.max(px(tr.s2) - px(tr.s1), 1)}
                      height={GROS + 4} fill="transparent">
                  <title>
                    {`${tr.eticheta} · s = ${mFmt(tr.s1)}…${mFmt(tr.s2)} m\n` +
                     tr.trasee.map((t) => `${t.n}Ø${Math.round(t.phi_mm)} — ${t.rol}`)
                              .join("\n")}
                  </title>
                </rect>
              </g>
            ))}

            {/* REPERELE: de aici înainte lucrează pachetul ăsta */}
            {repere.map(({ tr, x, y: yt }, i) => (
              <g key={`r${i}`}>
                <line x1={px(tr.s1)} y1={yt + 2} x2={px(tr.s1)}
                      y2={yBara - GROS / 2 - 1} stroke={BARA}
                      strokeWidth={0.8} strokeDasharray="2 2" opacity={0.7} />
                <text x={x} y={yt} fontSize={9.5} fontFamily="var(--mono)"
                      fill={BARA}>
                  {tr.eticheta}
                </text>
              </g>
            ))}

            {/* SUPRAPUNERILE: unde o bucată de bară o prinde pe următoarea */}
            {f.trasee.flatMap((t) =>
              (t.innadiri || []).map((n, k) => {
                const x1 = px(n.s_m);
                const x2 = px(n.s2_m);
                const w = Math.max(x2 - x1, 2);
                const col = n.fortata ? INNADIRE_FORTATA : INNADIRE;
                return (
                  <g key={`${t.rol}-${k}-${n.s_m}`}>
                    <title>
                      {tt("struct.bare.suprapunere", {
                        bare: `${t.n}Ø${Math.round(t.phi_mm)}`,
                        l0: mFmt(n.l0_m),
                        s: mFmt(n.s_m),
                      }) +
                       tt("struct.bare.zona", { zona: n.zona }) +
                       (n.fortata ? tt("struct.bare.fortata") : "")}
                    </title>
                    <rect x={x1} y={yBara - GROS / 2} width={w} height={GROS}
                          fill={col} opacity={0.75} />
                    <path d={`M${x1} ${yBara + GROS / 2 + 5} V${yBara + GROS / 2 + 1}` +
                             ` H${x1 + w} V${yBara + GROS / 2 + 5}`}
                          fill="none" stroke={col} strokeWidth={1.2} />
                    {w > 26 && (
                      <text x={x1 + w / 2} y={yBara + GROS / 2 + 13}
                            textAnchor="middle" fontSize={8.5}
                            fontFamily="var(--mono)" fill={col}>
                        {`l₀ ${mFmt(n.l0_m)}`}
                      </text>
                    )}
                  </g>
                );
              })
            )}
          </g>
        );
      })}

      {/* abscisa: aceleași repere ca planșa de deasupra */}
      <line x1={px(0)} y1={H - 12} x2={px(Ltot)} y2={H - 12}
            stroke={COTA_MARTOR} strokeWidth={0.9} />
      {noduri.map((s, i) => (
        <text key={`c${i}`} x={px(s)} y={H - 2} fontSize={9}
              textAnchor={i === 0 ? "start" : i === noduri.length - 1 ? "end" : "middle"}
              fontFamily="var(--mono)" fill="var(--mut)">
          {mFmt(s, 2)}
        </text>
      ))}
    </svg>
  );
}
